import { useCallback, useMemo, useState } from "react"

import {
  getProviderDaily,
  getProviderDetail,
  postProviderDaily,
  putProviderDaily,
} from "helpers/backend_helper"
import getStorageUser from "helpers/api_helper"

import useAvailabilityBoard from "common/availability/hooks/useAvailabilityBoard"

/**
 * Alta de disponibilidad desde el portal del proveedor. Sobre el núcleo
 * compartido cambian dos cosas: no hay selector de proveedor —es el propio
 * usuario, y el backend toma su `enterprise_id` del token— y rige la ventana
 * diaria de captura.
 */
export default function useProviderAvailability() {
  const enterpriseId = useMemo(() => getStorageUser()?.enterprise_id, [])

  // null mientras no se sabe: evita pintar la cuenta atrás antes de tiempo
  const [secondsLeft, setSecondsLeft] = useState(null)
  const [isExpired, setIsExpired] = useState(false)

  const load = useCallback(async () => {
    const [diaria, detalle] = await Promise.all([
      getProviderDaily(),
      getProviderDetail(enterpriseId),
    ])

    // la API devuelve minutos restantes de la ventana de captura
    const restantes = Number(diaria?.time || 0) * 60
    setSecondsLeft(restantes)
    setIsExpired(restantes <= 0)

    return {
      dates: diaria?.dates || [],
      dailies: diaria?.data || [],
      farms: detalle?.farms || [],
    }
  }, [enterpriseId])

  const create = useCallback(
    (registro, { date }) => postProviderDaily({ date, data: [registro] }),
    []
  )

  const update = useCallback(
    (registro, { date }) =>
      putProviderDaily({
        date,
        daily_availability_id: registro.id,
        data: [registro],
      }),
    []
  )

  const board = useAvailabilityBoard({ load, create, update })

  const onExpire = useCallback(() => setIsExpired(true), [])

  // Última barrera: aunque la interfaz ya esconde los botones, un render
  // obsoleto no debe poder escribir fuera de la ventana.
  const onCreate = async registro => {
    if (isExpired) return false
    return board.events.onCreate(registro)
  }

  const onUpdate = async registro => {
    if (isExpired) return false
    return board.events.onUpdate(registro)
  }

  return {
    state: { ...board.state, secondsLeft, isExpired },
    events: { ...board.events, onCreate, onUpdate, onExpire },
  }
}
